import React from 'react';
import { Spin } from 'antd';
import { useAuth, useIsAuthenticated, useAuthLoading } from '../../auth/AuthContext';
import { LoginPage } from './LoginPage';

interface ProtectedRouteProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

/**
 * Protected route component that handles authentication requirements
 * Shows login page if authentication is required but user is not authenticated
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  fallback 
}) => {
  const { isAuthEnabled } = useAuth();
  const isAuthenticated = useIsAuthenticated();
  const isLoading = useAuthLoading();

  // If authentication is disabled, always show children
  if (!isAuthEnabled) {
    return <>{children}</>;
  }

  // Show loading spinner while checking authentication
  if (isLoading) {
    // Check for dark mode preference
    const isDarkMode = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    
    return (
      fallback || (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          background: isDarkMode ? '#242424' : 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
        }}>
          <div style={{
            background: isDarkMode ? 'rgba(40, 40, 40, 0.95)' : 'rgba(255, 255, 255, 0.9)',
            padding: '40px',
            borderRadius: '16px',
            textAlign: 'center',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)'
          }}>
            <Spin size="large" style={{ color: '#646cff' }} />
            <div style={{ 
              marginTop: 16, 
              color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : '#666' 
            }}>
              Checking authentication...
            </div>
          </div>
        </div>
      )
    );
  }

  // If authenticated, show children
  if (isAuthenticated) {
    return <>{children}</>;
  }

  // If not authenticated, show login page
  return <LoginPage />;
};

/**
 * Hook to programmatically check if route access is allowed
 */
export const useRouteAccess = () => {
  const { isAuthEnabled } = useAuth();
  const isAuthenticated = useIsAuthenticated();
  const isLoading = useAuthLoading();

  return {
    isAllowed: !isAuthEnabled || isAuthenticated,
    isLoading,
    requiresAuth: isAuthEnabled,
  };
}; 